import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { ImpactBadge, DocTypeBadge, formatISODate } from '../components/badges';
import RelatedQmsKnowledge from '../components/RelatedQmsKnowledge';
import SendToKnowledgeCenter from '../components/SendToKnowledgeCenter';
import { API_BASE_URL } from '../config/api';
import { LoadingSkeleton } from '../components/LoadingSkeleton';
import { usePageMeta } from '../hooks/usePageMeta';

// ── Types ────────────────────────────────────────────────────────────

interface QmsClauseRef {
  framework: string;
  clause: string;
  title: string | null;
}

interface QmsNewsItem {
  id: number;
  source: string;
  external_id: string;
  title: string | null;
  summary: string | null;
  document_type: string | null;
  framework: string | null;
  impact_level: string | null;
  affected_clauses: QmsClauseRef[] | null;
  url: string | null;
  published_date: string | null;
}

// ── Tab config ────────────────────────────────────────────────────────

const FRAMEWORK_TABS = [
  { key: 'all', label: 'All' },
  { key: 'BRCGS', label: 'BRCGS' },
  { key: 'FSSC 22000', label: 'FSSC 22000' },
  { key: 'IFS', label: 'IFS' },
  { key: 'SQF', label: 'SQF' },
  { key: 'GFSI', label: 'GFSI' },
  { key: 'Codex', label: 'Codex' },
  { key: 'ISO 22000', label: 'ISO 22000' },
] as const;

type FrameworkFilter = typeof FRAMEWORK_TABS[number]['key'];

// ── Labels & badge classes ────────────────────────────────────────────

const SOURCE_LABELS: Record<string, string> = {
  gfsi: 'MyGFSI',
  brcgs: 'BRCGS',
  fssc22000: 'Foundation FSSC',
  ifs: 'IFS',
  sqf: 'SQFI',
  codex: 'Codex Alimentarius',
  iso_tc34_sc17: 'ISO/TC 34/SC 17',
};

const FRAMEWORK_BADGE_CLASS: Record<string, string> = {
  'BRCGS': 'source-badge--brcgs',
  'FSSC 22000': 'source-badge--fssc',
  'IFS': 'source-badge--ifs-fw',
  'SQF': 'source-badge--sqf',
  'GFSI': 'source-badge--gfsi',
  'Codex': 'source-badge--codex',
  'ISO 22000': 'source-badge--iso',
  'General': 'source-badge--qms-general',
};

// curated.qms_clauses framework keys -> human labels
const CLAUSE_FRAMEWORK_LABELS: Record<string, string> = {
  BRC_V9: 'BRC v9',
  HACCP: 'HACCP',
  IFS: 'IFS',
};

// ── Data fetching ────────────────────────────────────────────────────

const baseUrl = API_BASE_URL;

async function fetchQmsNews(): Promise<QmsNewsItem[]> {
  const res = await fetch(`${baseUrl}/api/qms-news?days=365`, { cache: 'no-store' });
  if (!res.ok) throw new Error(await res.text());
  const data = await res.json() as { ok: boolean; items: QmsNewsItem[] };
  return data.items;
}

// ── Component ────────────────────────────────────────────────────────

const PAGE_SIZE = 25;

export default function QmsNewsPage() {
  usePageMeta({ title: 'QMS and certification news', description: 'Standard revisions, position statements, audit protocol changes and benchmarking decisions from BRCGS, FSSC 22000, IFS, SQF, GFSI, Codex and ISO.' });
  const [activeTab, setActiveTab] = useState<FrameworkFilter>('all');
  const [openIdx, setOpenIdx] = useState<number | null>(null);
  const [page, setPage] = useState(0);

  const { data, isLoading, isError, error } = useQuery<QmsNewsItem[], Error>(
    ['qmsNews'], fetchQmsNews,
  );

  const items = data ?? [];
  const filtered = activeTab === 'all'
    ? items
    : items.filter(i => i.framework === activeTab);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const paginated = filtered.slice(page * PAGE_SIZE, (page + 1) * PAGE_SIZE);

  const goToPage = (p: number) => {
    setPage(p);
    setOpenIdx(null);
    window.scrollTo({ top: 0 });
  };

  return (
    <section className="news-page qms-page">
      <h1>QMS &amp; Certification News</h1>
      <p className="qms-page__intro">
        Standard revisions, position statements, audit protocol changes and
        benchmarking decisions from the major certification scheme owners.
      </p>

      {/* Framework tab bar */}
      <div className="news-page__tabs">
        {FRAMEWORK_TABS.map(tab => (
          <button
            key={tab.key}
            className={`news-page__tab ${activeTab === tab.key ? 'news-page__tab--active' : ''}`}
            onClick={() => { setActiveTab(tab.key); setOpenIdx(null); setPage(0); }}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {isLoading && <LoadingSkeleton rows={8} title={false} />}
      {isError && <div className="error-state">Error: {error?.message}</div>}

      {!isLoading && !isError && filtered.length === 0 && (
        <div className="loading-state">No QMS news items found.</div>
      )}

      <ul className="feed-list">
        {paginated.map((item, i) => {
          const isOpen = openIdx === i;
          return (
            <li key={item.id} className={`feed-item ${isOpen ? 'feed-item--open' : ''}`}>
              <QmsCard item={item} isOpen={isOpen} onToggle={() => setOpenIdx(isOpen ? null : i)} />
            </li>
          );
        })}
      </ul>

      {!isLoading && !isError && totalPages > 1 && (
        <div className="rasff-deepdive__pagination">
          <button className="rasff-dash__tf-btn" onClick={() => goToPage(0)} disabled={page === 0}>«</button>
          <button className="rasff-dash__tf-btn" onClick={() => goToPage(Math.max(0, page - 1))} disabled={page === 0}>‹ Prev</button>
          <span className="rasff-deepdive__pagination-info">
            Page {page + 1} of {totalPages}
            <span style={{ color: 'var(--color-text-secondary)', marginLeft: 8 }}>
              ({filtered.length} items)
            </span>
          </span>
          <button className="rasff-dash__tf-btn" onClick={() => goToPage(Math.min(totalPages - 1, page + 1))} disabled={page >= totalPages - 1}>Next ›</button>
          <button className="rasff-dash__tf-btn" onClick={() => goToPage(totalPages - 1)} disabled={page >= totalPages - 1}>»</button>
        </div>
      )}
    </section>
  );
}

// ── QMS Card ─────────────────────────────────────────────────────────

function QmsCard({ item, isOpen, onToggle }: {
  item: QmsNewsItem; isOpen: boolean; onToggle: () => void;
}) {
  const framework = item.framework || 'General';
  const badgeClass = FRAMEWORK_BADGE_CLASS[framework] || 'source-badge--qms-general';
  const docType = item.document_type ? item.document_type.replace(/_/g, ' ') : null;
  const clauses = item.affected_clauses ?? [];

  return (
    <>
      <button className="feed-item__btn" onClick={onToggle} aria-expanded={isOpen}>
        <span className={`source-badge ${badgeClass}`}>{framework}</span>
        <ImpactBadge level={item.impact_level} />
        <DocTypeBadge type={docType} />
        <span className="feed-item__title">{item.title}</span>
        <span className="feed-item__date">{formatISODate(item.published_date)}</span>
        <span className={`feed-item__chevron ${isOpen ? 'feed-item__chevron--open' : ''}`} aria-hidden="true" />
      </button>

      {isOpen && (
        <div className="rasff-details">
          {item.summary && (
            <div className="feed-item__summary">
              <strong>Summary:</strong> {item.summary}
            </div>
          )}
          <div className="rasff-details__grid">
            <div className="rasff-detail">
              <span className="rasff-detail__label">Publisher</span>
              <span className="rasff-detail__value">{SOURCE_LABELS[item.source] || item.source}</span>
            </div>
            {docType && (
              <div className="rasff-detail">
                <span className="rasff-detail__label">Document type</span>
                <span className="rasff-detail__value">{docType}</span>
              </div>
            )}
          </div>
          {clauses.length > 0 && (
            <div className="rasff-detail rasff-detail--full">
              <span className="rasff-detail__label">Affected clauses</span>
              <span className="rasff-detail__value">
                {clauses.map((c, j) => (
                  <span key={j} className="feed-item__product-tag" title={c.title ?? undefined}>
                    {CLAUSE_FRAMEWORK_LABELS[c.framework] || c.framework} · {c.clause}
                    {c.title ? ` — ${c.title}` : ''}
                  </span>
                ))}
              </span>
            </div>
          )}
          <RelatedQmsKnowledge qmsId={item.id} />
          <div className="rasff-portal-link qms-card__footer">
            {item.url && (
              <a href={item.url} target="_blank" rel="noopener noreferrer">
                View source &rarr;
              </a>
            )}
            <SendToKnowledgeCenter url={item.url} />
          </div>
        </div>
      )}
    </>
  );
}
